<template>
  <div class="w-full h-full flex align-middle justify-center">
    <video
      ref="videoPlayer"
      controls
      muted
      preload="none" >
      <source ref="videoSourceElement" :src="videoSource" type="video/mp4" />
      <track kind="captions" srclang="en" label="English" />
      {{ this.$t('popUpVideo.browserDoesNotSupportVideoTag') }}
      <slot />
    </video>
  </div>
</template>

<script>
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import plyrSvg from '@/assets/svg/plyr.svg';
import useTransitStore from '@/stores/TransitStore';

export default {
  name: 'VideoPlayer',
  props: {
    selectedVideoData: {
      type: Object,
      required: true,
      validator: (value) => Object.prototype.hasOwnProperty.call(value, 'streamId')
                            && Object.prototype.hasOwnProperty.call(value, 'transitId')
                            && Object.prototype.hasOwnProperty.call(value, 'duration'),
    },
    options: {
      type: Object,
      default: () => ({
        controls: ['play', 'progress', 'current-time', 'fullscreen'],
        autoplay: false,
        iconUrl: plyrSvg,
        blankVideo: '',
        invertTime: false,
        toggleInvert: true,
        preload: 'none',
      }),
    },
  },
  data() {
    return {
      player: null,
      isReloading: false,
      videoSource: '',
      lastPlaybackTime: 0,
      isPlaying: false,
    };
  },
  setup() {
    return {
      transitStore: useTransitStore(),
    };
  },
  methods: {
    async loadNewVideoSource() {
      if (this.isReloading) {
        return;
      }
      this.isReloading = true;
      try {
        if (this.selectedVideoData) {
          const newUrl = await this.transitStore.getVideoInlineUrl(
            this.selectedVideoData.streamId,
            this.selectedVideoData.transitId,
          );
          if (newUrl) {
            this.player = new Plyr(this.$refs.videoPlayer, this.options);
            this.player.on('ready', this.handlePlayerReady);
            this.player.on('error', this.handlePlayerError);
            this.player.on('timeupdate', this.handleTimeUpdate);
            this.player.on('loadeddata', this.handleLoadFinished);
            this.player.on('playing', () => this.handlePlayerState(true));
            this.player.on('pause', () => this.handlePlayerState(false));

            this.videoSource = newUrl;
            this.$nextTick(() => {
              if (this.$refs.videoPlayer) {
                this.$refs.videoPlayer.load();
                this.player.currentTime = this.lastPlaybackTime;
              }
            });
          } else {
            console.error('Could not get a valid URL.');
          }
        }
      } catch (error) {
        this.$emit('fetching-error', error);
      } finally {
        this.isReloading = false;
      }
    },
    async handlePlayerError() {
      console.error('Plyr error recieved. Attempting to reload video source.');
      await this.loadNewVideoSource();
    },
    handlePlayerReady() {
      this.player.play();
    },
    handleTimeUpdate() {
      this.$emit('time-changed', this.player.currentTime);
      if (this.player.currentTime > 0) {
        this.lastPlaybackTime = this.player.currentTime;
      }
    },
    handleLoadFinished() {
      this.player.currentTime = this.lastPlaybackTime;
      if (this.isPlaying) {
        this.player.play();
      }
    },
    handlePlayerState(playing) {
      this.isPlaying = playing;
    },
    seekTo(timeInSeconds) {
      if (this.player) {
        this.player.currentTime = timeInSeconds;
      }
    },
  },
  async mounted() {
    await this.loadNewVideoSource();
    this.emitter.on('current-time-changed', this.seekTo);
  },
  unmounted() {
    this.emitter.off('current-time-changed');
  },
  beforeUnmount() {
    if (this.player) {
      this.player.off('ready', this.handlePlayerReady);
      this.player.off('error', this.handlePlayerError);
      this.player.off('timeupdate', this.handleTimeUpdate);
      this.player.off('loadeddata', this.handleLoadFinished);
      this.player.on('playing');
      this.player.on('pause');
    }
  },
  watch: {
    selectedVideoData: {
      async handler(newVal, oldVal) {
        if (newVal && oldVal && (newVal.streamId !== oldVal.streamId || newVal.transitId !== oldVal.transitId)) {
          await this.loadNewVideoSource();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
::v-deep(.plyr) {
  width: 100% !important;
  height: 100% !important;
}

::v-deep(.plyr__video-wrapper) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}
</style>
